import PropTypes from 'prop-types';
import { useState, useEffect } from 'react';
import { useParams } from 'react-router';
import { Box, CircularProgress, Typography } from '@mui/material';
import QuadPanel from 'features/IRWorkstation/components/QuadPanel';
import inputSignalsService from 'services/inputSignalsService';
import NewsFeedRow from 'features/IRWorkstation/components/NewsFeedRow';

const NewsFeedWidget = ({ onOpenNewsFeedDrawer }) => {
	const { tenantId } = useParams();
	const [loading, setLoading] = useState(true);
	const [newsFeeds, setNewsFeeds] = useState([]);
	const [error, setError] = useState(null);

	useEffect(() => {
		const fetchNewsFeeds = async () => {
			try {
				setLoading(true);
				setError(null);

				const newsFeeds = await inputSignalsService.getInputSignals({
					event_category: ['vdr', 'signal', 'news', 'report'],
					event_status: 'unacknowledged',
				});

				setNewsFeeds(newsFeeds);
			} catch (err) {
				console.error('Failed to fetch input signals:', err);
				setError('Failed to load news feed.');
			} finally {
				setLoading(false);
			}
		};

		fetchNewsFeeds();
	}, [tenantId]);

	const NewsFeedList = () =>
		newsFeeds.map((newsFeed) => (
			<NewsFeedRow
				key={`news-feed-${newsFeed.id}`}
				newsFeed={newsFeed}
				onClick={newsFeed ? () => onOpenNewsFeedDrawer(newsFeed) : undefined}
			/>
		));

	const LoadingIndicator = () => (
		<Box
			sx={{
				display: 'flex',
				justifyContent: 'center',
				alignItems: 'center',
				padding: '40px',
				flexDirection: 'column',
				gap: 2,
				height: '100%',
			}}
		>
			<CircularProgress size={18} />
			<Typography variant='body2' color='text.secondary'>
				Loading news feed...
			</Typography>
		</Box>
	);

	const ErrorMessage = () => (
		<Box
			sx={{
				display: 'flex',
				justifyContent: 'center',
				alignItems: 'center',
				padding: '40px',
				height: '100%',
			}}
		>
			<Typography variant='body2' color='error'>
				{error}
			</Typography>
		</Box>
	);

	const EmptyState = () => (
		<Box
			sx={{
				display: 'flex',
				justifyContent: 'center',
				alignItems: 'center',
				padding: '40px',
				height: '100%',
			}}
		>
			<Typography variant='body2' color='text.secondary'>
				No news feed.
			</Typography>
		</Box>
	);

	return (
		<QuadPanel
			title='News & activity feed'
			badge={`${newsFeeds.length} new`}
			badgeColor='blue'
			subtitle='↔ VDR-tagged items sourced from Raise VDR signal engine'
		>
			{loading ? (
				<LoadingIndicator />
			) : error ? (
				<ErrorMessage />
			) : newsFeeds.length === 0 ? (
				<EmptyState />
			) : (
				<NewsFeedList />
			)}
		</QuadPanel>
	);
};

NewsFeedWidget.propTypes = {
	onOpenNewsFeedDrawer: PropTypes.func.isRequired,
};

export default NewsFeedWidget;
